import api from 'api';
import useCompanyConfig from 'hooks/useCompanyConfig';
import {ReactNode, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import toast from 'react-hot-toast';
import {QueryFunctionContext, useMutation, useQuery} from 'react-query';
import {useHistory, useParams} from 'react-router-dom';
import {SingleValue} from 'react-select';
import {Address, Customer, ListResponse, Request, SelectOption} from 'types';

type FormValues = {
  customer_id: number;
  address_id: number;
  source_id: number;
  drugs?: string[];
  property_id?: number;
  description?: string;
  pickup_id?: number;
};

async function getVendors() {
  const params = {
    limit: 150,
    client_role: 'vendors',
  };
  const {data} = await api.get<ListResponse<Customer[]>>('/customers/', {
    params,
  });
  return data?.results as unknown as Customer[];
}

const getCustomerAddresses = async (id: number) => {
  try {
    const {data} = await api.get<Customer>(`/customers/${id}/`);
    return data?.addresses || [];
  } catch (error) {
    console.log(error);
  }
};
type CreatePurchaseOrderPayload = {
  customer_id: number;
  address_id: number;
  source_id: number;
  drugs?: number[];
  property_id?: number;
  description?: string;
};

async function createPurchaseOrder(payload: CreatePurchaseOrderPayload) {
  const body = Object.fromEntries(
    Object.entries(payload).filter(([, value]) => Boolean(value))
  );
  return await api.post<any>('/requests/create-order/', body);
}
type AttachPurchaseOrderToRequestPayload = {
  pickup_id: number;
  dropoff_id: number;
};
async function attachPurchaseOrderToRequest({
  pickup_id,
  dropoff_id,
}: AttachPurchaseOrderToRequestPayload) {
  return await api.post(
    `/requests/${dropoff_id}/assign-pickup-order/${pickup_id}/`
  );
}
async function getNotAttachedPurchaseOrders({
  queryKey,
}: QueryFunctionContext<any[]>) {
  const [, search, vendorId] = queryKey;
  const params: any = {
    limit: 100,
    order_type: 'pickup',
    has_source_id: false,
    customer_id: vendorId,
  };
  if (search) {
    params.search = search;
  }
  const {data} = await api.get<ListResponse<Request>>('/requests', {
    params,
  });
  return data?.results;
}
export const useCreatePurchaseOrder = ({
  orderId,
  orderItems,
  onCloseModal,
}: {
  orderId: number;
  orderItems: number[];
  onCloseModal: () => void;
}) => {
  const {push} = useHistory();
  const {id: sourceId} = useParams<{id: string}>();
  const [selectedVendor, setSelectedVendor] =
    useState<SingleValue<SelectOption<string | number, ReactNode>>>();
  const form = useForm<FormValues>();
  const [customerAddresses, setCustomerAddresses] = useState<Address[]>([]);
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [search] = useState('');
  const {isPickupDropoff} = useCompanyConfig();

  const {data: vendorList, refetch: refetchVendorList} = useQuery(
    'vendor-list',
    getVendors
  );
  const {data: notAttachedPurchaseOrders} = useQuery({
    queryKey: ['not-attached-purchase-orders', search, selectedVendor?.value],
    queryFn: getNotAttachedPurchaseOrders,
    enabled: Boolean(isPickupDropoff && selectedVendor?.value),
  });
  const {mutate: getCustomerAddressesMutation} = useMutation({
    mutationFn: getCustomerAddresses,
    onSuccess: (data) => {
      setCustomerAddresses(data as unknown as Address[]);
    },
  });
  const {
    mutate: createPurchaseOrderMutation,
    isLoading: isCreatingPurchaseOrder,
  } = useMutation({
    mutationFn: createPurchaseOrder,
    onSuccess: () => {
      toast.success('Purchase order created successfully');
      onCloseModal();
      push(`/orders/${sourceId}?order_type=pickup`);
    },
  });

  const {
    mutate: attachPurchaseOrderToRequestMutation,
    isLoading: isAttachingPurchaseOrder,
  } = useMutation({
    mutationFn: attachPurchaseOrderToRequest,
    onSuccess: () => {
      toast.success('Purchase order attached successfully');
      onCloseModal();
      push(`/orders/${sourceId}?order_type=pickup`);
    },
  });

  const vendorOptions = useMemo(
    () =>
      vendorList?.map((item) => ({
        label: item?.customer_company,
        value: item.id,
      })),
    [vendorList]
  );

  const addressOptions = useMemo(
    () =>
      customerAddresses?.map((item) => ({
        value: item.id,
        label: item.display_address,
      })),
    [customerAddresses]
  );

  const purchaseOrderOptions = useMemo(() => {
    return notAttachedPurchaseOrders?.map((item) => ({
      value: item?.id,
      label: `#${item?.sequential_order_number} - (${item?.drug_name})`,
    }));
  }, [notAttachedPurchaseOrders]);

  const handleCreatePurchaseOrder = () => {
    if (form.watch('pickup_id')) {
      attachPurchaseOrderToRequestMutation({
        pickup_id: Number(form.watch('pickup_id')),
        dropoff_id: orderId,
      });
    } else {
      createPurchaseOrderMutation({
        customer_id: Number(selectedVendor?.value),
        address_id: Number(form.watch('address_id')),
        drugs: orderItems,
        source_id: orderId,
      });
    }
  };

  return {
    vendorOptions,
    selectedVendor,
    setSelectedVendor,
    getCustomerAddressesMutation,
    handleCreatePurchaseOrder,
    isCreatingPurchaseOrder,
    isAttachingPurchaseOrder,
    addressOptions,
    form,
    vendorModalOpen,
    setVendorModalOpen,
    addressModalOpen,
    setAddressModalOpen,
    refetchVendorList,
    purchaseOrderOptions,
  };
};
